import { useCallback, useEffect, useRef } from 'react';
import { candidateActionFor, candidateActionsIn } from '../TagManager.utils';
import { CandidateStandard } from '../types';

// A slot is a position, so an intent recorded before an add resolves to whatever row moves up into
// it — the next candidate's Add button. A tagId is for undo, where a standard comes back to the list.
type CandidateFocusIntent = { slot: number } | { tagId: number } | { search: true };

interface UseCandidateFocusParams {
  candidates: CandidateStandard[];
  isLoading: boolean;
}

/**
 * Focus inside the Add panel moves for the same reason the table's does: the button that was clicked
 * is gone by the time the new list renders. The search field is the fallback because it is the one
 * control that survives every state of the panel.
 */
export default function useCandidateFocus({ candidates, isLoading }: UseCandidateFocusParams) {
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const pendingFocusRef = useRef<CandidateFocusIntent | undefined>({ slot: 0 });

  const recordSlotIntent = useCallback((slot: number) => {
    pendingFocusRef.current = { slot };
  }, []);

  const recordTagIntent = useCallback((candidate: CandidateStandard) => {
    pendingFocusRef.current = { tagId: candidate.tag_id };
  }, []);

  // After a batch there is no row to return to: the bar and its button are both gone.
  const recordSearchIntent = useCallback(() => {
    pendingFocusRef.current = { search: true };
  }, []);

  const clearIntent = useCallback(() => {
    pendingFocusRef.current = undefined;
  }, []);

  // Nothing refetches after a failure, so there is no intent to consume — put focus back on the
  // button that was pressed instead.
  const onFailure = useCallback((candidate: CandidateStandard) => {
    clearIntent();
    window.requestAnimationFrame(() => candidateActionFor(panelRef.current, candidate.tag_id)?.focus());
  }, [clearIntent]);

  // The mount intent above is what lands focus on the first Add button when the panel opens; it waits
  // here until the first page has actually rendered.
  useEffect(() => {
    const intent = pendingFocusRef.current;
    if (!intent || isLoading) return;

    pendingFocusRef.current = undefined;

    let target: HTMLElement | null | undefined;

    if ('search' in intent) target = searchRef.current;
    else if ('tagId' in intent) target = candidateActionFor(panelRef.current, intent.tagId);
    else target = candidateActionsIn(panelRef.current)[intent.slot];

    (target ?? searchRef.current)?.focus();
  }, [candidates, isLoading]);

  return {
    clearIntent, onFailure, panelRef, recordSearchIntent, recordSlotIntent, recordTagIntent, searchRef,
  };
}
